import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldCheck, MessageCircle, Phone, Check } from "lucide-react";
import { API, STORE, waLink, resolveImg, OFFER } from "@/lib/store";
import { BikeCard } from "@/components/BikeCard";

export default function BikeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bike, setBike] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/bikes/${id}`)
      .then((r) => {
        setBike(r.data);
        document.title = `${r.data.name} | Knoble Rides Lagos`;
        axios.get(`${API}/bikes?category=${r.data.category}`).then((res) =>
          setRelated(res.data.filter((b) => b.id !== id).slice(0, 3))
        );
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="font-display text-3xl font-bold">Bike not found</h1>
        <Link to="/bikes" className="mt-4 inline-block font-semibold text-[#8C5A32]">← Back to catalog</Link>
      </div>
    );
  }
  if (!bike) return <div className="py-32 text-center text-zinc-500">Loading…</div>;

  const msg = `Hello ${STORE.name}, I'm interested in the ${bike.name} (${bike.category_name}). Please share availability, price and pickup details.`;

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-[#8C5A32]">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      </div>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-3xl border border-[#E6D7C8] bg-[#F1ECE6]">
            {bike.image_url ? (
              <img src={resolveImg(bike.image_url)} alt={`${bike.name} - ${bike.category_name}`} className="aspect-square w-full object-cover" />
            ) : (
              <div className="flex aspect-square items-center justify-center text-zinc-400">Photo coming soon</div>
            )}
          </div>

          <div className="flex flex-col">
            <span className="inline-flex w-fit rounded-full bg-[#0B0A09] px-3 py-1 text-xs font-semibold text-white">{bike.category_name}</span>
            <h1 className="mt-4 font-display text-3xl font-extrabold text-[#0B0A09] sm:text-4xl">{bike.name}</h1>
            {bike.price_display && <p className="mt-3 text-xl font-bold text-[#8C5A32]">{bike.price_display}</p>}
            {bike.description && <p className="mt-5 text-base leading-relaxed text-zinc-600">{bike.description}</p>}

            {bike.spec && (
              <div className="mt-6 rounded-2xl border border-[#E6D7C8] bg-white p-5">
                <h3 className="font-display text-sm font-bold uppercase tracking-wider text-zinc-500">Specifications</h3>
                <ul className="mt-3 space-y-2">
                  {bike.spec.split(",").map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#8C5A32]" /> {s.trim()}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 flex items-center gap-2 rounded-2xl bg-[#FDF8F3] px-5 py-4 ring-1 ring-[#E6D7C8]">
              <ShieldCheck className="h-6 w-6 shrink-0 text-[#8C5A32]" />
              <p className="text-sm font-semibold text-[#8C5A32]">{OFFER} with this purchase.</p>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                data-testid={`bike-detail-enquire-${bike.id}`}
                href={waLink(msg)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-wa inline-flex flex-1 items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-bold"
              >
                <MessageCircle className="h-5 w-5" /> Enquire on WhatsApp
              </a>
              <a href={STORE.phoneTel} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#E6D7C8] px-6 py-4 text-base font-bold text-[#0B0A09] hover:bg-[#FDF8F3]">
                <Phone className="h-5 w-5 text-[#8C5A32]" /> Call
              </a>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-bold text-[#0B0A09]">More {bike.category_name}s</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((b) => <BikeCard key={b.id} bike={b} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
